import React, { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { fetchGraphData, fetchHeadlineData, intializeChartsAction } from '../../../../store/dashboard/actions';
import { initializeDatepickerDates } from '../../../../utils/dateUtils';
import { GST_STATUS } from '../../../../store/investor/constants';
import { TabModule } from '../../../../utils/constants/commonConstants';
import { FEATURES } from '../../../../store/user/constants';
import StatCard from '../../components/StatCard/StatCard';
import Graph from '../../components/Graph/Graph';
import DataSource from '../../components/DataSource/DataSource';
import TypeSelector from '../../components/TypeSelector/TypeSelector';
import AicaOverview from '../../../../common/AicaOverview/AicaOverview';
import RevenueTables from '../RevenueTables/RevenueTables';
import { DASHBOARD_DATA_TYPES, dataTypeSelectorOptions } from '../../dashboardConstants';
import { mixpanelRegisterDataTypeChange } from '../../../../store/dashboard/dashboard_mixpanel';
import Button from '../../../../common/_custom/Button/Button';
import MonthYearPicker from '../../../../common/_custom/MonthYearPicker/MonthYearPicker';
import { GST_RANGE_KEYS, useGstDateRange } from './useGstDateRange';
import dashboardStyles from '../../Dashboard.module.scss';
import styles from './GstOverview.module.scss';

interface GstAnalysisPanelProps {
    // `tab` is the polus tabName — kept as the legacy id since it's the API contract.
    tab: typeof TabModule.REVENUE | typeof TabModule.VENDOR_EXPENSES;
    isPDFReport?: boolean;
    pdfReportSection?: string;
}

// Segmented toggle options for the "Time Period" control (RECUR-27384). All
// is the default — see useGstDateRange for what each key maps to.
const GST_RANGE_TOGGLE_OPTIONS = [
    { id: GST_RANGE_KEYS.ALL, label: 'All' },
    { id: GST_RANGE_KEYS.LAST_FY, label: 'Last FY' },
    { id: GST_RANGE_KEYS.LAST_12M, label: 'Last 12 M' },
];

function GstAnalysisPanel({ tab, isPDFReport = false, pdfReportSection = '' }: GstAnalysisPanelProps) {
    const dispatch = useAppDispatch();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const dashboard = useAppSelector((state) => state.dashboard);
    const dashboardDataNew = dashboard.dashboardReducer;
    const investorData = useAppSelector((state) => state.investor);
    const periodSelectorData = useAppSelector((state) => state.periodSelector);
    const user = useAppSelector((state) => state.user);

    const dashboardConfig: any = investorData.config;
    const selectedCompany: any = investorData.selectedCompany;
    const headlinePanelData = dashboardDataNew?.headlineData?.data;
    const dataSource = dashboardDataNew?.dataSource || {};
    const chartsData = dashboardDataNew?.charts || {};
    const selectedTabConfig = dashboardConfig?.tabConfigs?.[tab];
    const chartsToShow: string[] = selectedTabConfig?.chartPanel?.cards || [];
    const showAlerts = !!user?.domainMetadata?.availableFeatures?.includes(FEATURES.ALERTS);

    // OR with EXPIRED is deliberate — historical chart data is renderable even
    // after the Vayana session lapses. Only orgs that never connected hit the
    // empty state. (DataSource pill counts only `connected` — different signal.)
    const gstList = dataSource?.[1]?.gstList || [];
    const noGSTConnected = !gstList.some((g: any) => g?.connected || g?.gstStatus === GST_STATUS.EXPIRED);

    const dataTypeQuery = searchParams.get('dataType');
    const dataType = dataTypeQuery || DASHBOARD_DATA_TYPES.CHARTS;

    const gstDateRange = useGstDateRange(tab);
    const rangeToggleHandlers: Record<string, () => void> = {
        [GST_RANGE_KEYS.ALL]: gstDateRange.selectAll,
        [GST_RANGE_KEYS.LAST_FY]: gstDateRange.selectLastFY,
        [GST_RANGE_KEYS.LAST_12M]: gstDateRange.selectLast12M,
    };
    const handleRangeToggle = (id: string) => rangeToggleHandlers[id]?.();

    const fetchHeadlinePanelData = (isPolling = false) => {
        // initializeDatepickerDates returns [fromDate, toDate, previousMonthLastDate].
        const [fromDate, , previousMonthLastDate] = initializeDatepickerDates(periodSelectorData.selectedPeriod.name);
        // An applied Time Period range takes over the headline window too — the backend
        // derives each card's value AND its month label from the last data month WITHIN
        // the window, so cards follow the filter. ALL (no fetch dates) keeps the legacy
        // FE-computed default window (RECUR-27384).
        const isRangeApplied = !!gstDateRange.fetchStartDate && !!gstDateRange.fetchEndDate;
        dispatch(
            fetchHeadlineData(
                {
                    tabName: tab,
                    startDate: isRangeApplied ? gstDateRange.fetchStartDate : fromDate,
                    endDate: isRangeApplied ? gstDateRange.fetchEndDate : previousMonthLastDate,
                    tenure: periodSelectorData.selectedPeriod.name || '',
                    reportId: selectedCompany?.reportId || '',
                },
                { isPolling },
            ),
        );
    };
    // The 10s headline poll below captures the closure that existed when the interval
    // was created; without this ref a poll tick after a range change would refetch with
    // the OLD window and overwrite the range-scoped cards (same frozen-closure class as
    // the GSTR3BTable poll fix, RECUR-27384). Assigned in an effect — not during render —
    // so under React 18 concurrent rendering an abandoned render can never leave the
    // interval pointing at a closure over uncommitted tab/range state.
    const fetchHeadlinePanelDataRef = useRef(fetchHeadlinePanelData);
    useEffect(() => {
        fetchHeadlinePanelDataRef.current = fetchHeadlinePanelData;
    });

    const fetchGraph = (graphName: string, isPollingCall = false) => {
        // ALL sends '' / '' (per-PNO config window, unchanged behavior). Any
        // applied Time Period range sends explicit month-snapped dates instead.
        dispatch(
            fetchGraphData(
                {
                    graphNames: [graphName],
                    tabName: tab,
                    startDate: gstDateRange.fetchStartDate,
                    endDate: gstDateRange.fetchEndDate,
                    tenure: periodSelectorData.selectedPeriod.name,
                    reportId: selectedCompany?.reportId || '',
                },
                { isPollingCall },
            ),
        );
    };

    const headlinePanelDataPollingRef: any = useRef();
    const initialiseHeadlinePanelPolling = () => {
        if (!headlinePanelDataPollingRef.current) {
            fetchHeadlinePanelDataRef.current(false);
            headlinePanelDataPollingRef.current = setInterval(() => fetchHeadlinePanelDataRef.current(true), 10000);
        }
    };
    useEffect(() => {
        return () => {
            if (headlinePanelDataPollingRef.current) {
                clearInterval(headlinePanelDataPollingRef.current);
                headlinePanelDataPollingRef.current = null;
            }
        };
    }, []);

    // `dataType` is intentionally NOT in the dep array — Chart↔Table toggle is
    // presentation-only; refiring `intializeChartsAction` on every toggle wipes
    // Redux `charts` and races with per-Graph refetches.
    const selectedPeriodName = periodSelectorData.selectedPeriod?.name;
    useEffect(() => {
        if (!params.id) return;
        if (!selectedPeriodName) return;
        dispatch(intializeChartsAction({ charts: chartsToShow }));
        fetchHeadlinePanelData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, params.id, selectedPeriodName]);

    // Applying a new Time Period range (toggle click / month pick / reset)
    // re-initializes charts — resetting each chart's isFetched so Graph
    // components refetch in-view via the latest `fetchGraph` closure above —
    // AND refires the headline cards, which now follow the applied range
    // (product decision: cards report the last data month within the range).
    // Skips the initial mount (that first fetch is already covered by the
    // tab-change effect).
    // Keyed only on the fetch dates (not rangeKey) — picking one side of a
    // CUSTOM range flips rangeKey to 'CUSTOM' before a fetch date is applied,
    // and that transition alone must not reset charts or refetch cards
    // (RECUR-27384 review fix).
    const isRangeMountRef = useRef(true);
    useEffect(() => {
        if (isRangeMountRef.current) {
            isRangeMountRef.current = false;
            return;
        }
        if (!params.id) return;
        dispatch(intializeChartsAction({ charts: chartsToShow }));
        fetchHeadlinePanelData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gstDateRange.fetchStartDate, gstDateRange.fetchEndDate]);

    const HEADLINE_PANEL_CONSTANTS = useMemo(() => ({ OPEN_LEGAL_CASES: 'Open Legal Cases', COMPLIANCES: 'compliances' }), []);

    const getGridRowHeight = (chartName: string, index: number) => {
        const pos = Math.floor(index / 2);
        const data = chartsData[chartName];
        const tileSize = data?.metadata?.tileSize || 0;
        if (tileSize > 1) return `${pos + 1} / ${pos + 1 + tileSize}`;
        return undefined;
    };

    const renderTable = () => (
        <RevenueTables
            tab={tab}
            dataSource={dataSource}
            fetchHeadlinePanelData={fetchHeadlinePanelData}
            rangeKey={gstDateRange.rangeKey}
            fetchStartDate={gstDateRange.fetchStartDate}
            fetchEndDate={gstDateRange.fetchEndDate}
        />
    );

    return (
        <>
            <div className={styles.AnalysisHeader}>
                <div className={styles.AnalysisHeaderLeft}>
                    <div className={styles.SectionTitle}>{dashboardConfig?.tabs?.find((t: any) => t.id === tab)?.label || tab}</div>
                </div>
                <div className={styles.AnalysisHeaderRight}>
                    <DataSource
                        dataSource={dataSource}
                        isLoading={dashboardDataNew.loadingHeadline}
                        selectedTab={tab}
                        isPDFReport={!!pdfReportSection}
                        orgId={params.id}
                    />
                </div>
            </div>

            <div className={styles.TimePeriodRow}>
                <div className={styles.TimePeriodRowLeft}>
                    <TypeSelector
                        testId="type-selector"
                        options={dataTypeSelectorOptions}
                        selectedOptionId={dataType}
                        changeSelectedOption={(id: string) => {
                            mixpanelRegisterDataTypeChange(id, tab, selectedCompany);
                            // Functional setter — avoids stale-closure race with the
                            // sibling `?subTab` writer in GstOverview.
                            setSearchParams((prev) => {
                                const next = new URLSearchParams(prev);
                                next.set('dataType', id);
                                return next;
                            });
                        }}
                    />
                    {/* Figma also shows a "Monthly ▾" grouping dropdown here — deliberately NOT built (dead feature, separate decision). */}
                </div>
                <div className={styles.TimePeriodRowRight}>
                    <span className={styles.TimePeriodLabel}>Time Period:</span>
                    <MonthYearPicker
                        value={gstDateRange.chipStartMonth}
                        min={gstDateRange.startPickerMin}
                        max={gstDateRange.startPickerMax}
                        onChange={gstDateRange.selectStartMonth}
                    />
                    <span className={styles.ToText}>to</span>
                    <MonthYearPicker
                        value={gstDateRange.chipEndMonth}
                        min={gstDateRange.endPickerMin}
                        max={gstDateRange.endPickerMax}
                        onChange={gstDateRange.selectEndMonth}
                    />
                    {gstDateRange.isRangeActive && (
                        <Button
                            text="Reset"
                            variant="secondary"
                            type="button"
                            className={styles.ResetButton}
                            onClick={gstDateRange.reset}
                        />
                    )}
                    <TypeSelector
                        testId="gst-range-toggle"
                        options={GST_RANGE_TOGGLE_OPTIONS}
                        selectedOptionId={gstDateRange.rangeKey}
                        changeSelectedOption={handleRangeToggle}
                    />
                </div>
            </div>

            <div className={styles.AnalysisBody}>
                {!isPDFReport && (
                    <AicaOverview style={{ marginTop: 0, marginBottom: '1rem' }} module={tab} isPDFReport={!!pdfReportSection} />
                )}

                {dataType === DASHBOARD_DATA_TYPES.CHARTS ? (
                    !dashboardDataNew.loadingHeadline && noGSTConnected ? (
                        <div className={dashboardStyles.NoGSTData}>
                            {tab === TabModule.REVENUE ? 'Revenue from GST' : 'Vendor Expenses from GST'}
                            <div className={dashboardStyles.Right}>Only Available for Connected GSTs</div>
                        </div>
                    ) : (
                        <>
                            <div className={clsx(dashboardStyles.Stats, { [dashboardStyles.StatsPDFReport]: isPDFReport })}>
                                {dashboardDataNew.loadingHeadline
                                    ? Array.from({ length: 4 }).map((_, index) => (
                                          <StatCard
                                              data={{}}
                                              key={`placeholder-${index}`}
                                              statName={`placeholder-${index}`}
                                              isLoading={true}
                                              isPDFReport={!!pdfReportSection}
                                          />
                                      ))
                                    : headlinePanelData &&
                                      Object.keys(headlinePanelData)
                                          .filter(
                                              (statName) =>
                                                  headlinePanelData[statName]?.title !== HEADLINE_PANEL_CONSTANTS.OPEN_LEGAL_CASES,
                                          )
                                          .map((statName) => (
                                              <StatCard
                                                  data={headlinePanelData?.[statName]}
                                                  key={statName}
                                                  statName={statName}
                                                  isLoading={false}
                                                  isPDFReport={!!pdfReportSection}
                                              />
                                          ))}
                            </div>
                            <div
                                className={clsx(dashboardStyles.GraphsContainer, {
                                    [dashboardStyles.GraphsContainerPDFReport]: isPDFReport,
                                })}
                            >
                                {chartsToShow.map((chartName, index) => (
                                    <div key={`${chartName}-${index}`} style={{ gridRow: getGridRowHeight(chartName, index) }}>
                                        {!!chartsData[chartName] &&
                                            (chartsData[chartName]?.isFetched && isPDFReport
                                                ? (chartsData[chartName]?.plots?.[0]?.plotValues || [])?.length ||
                                                  chartsData[chartName]?.plots?.[0]?.yearMonthMap
                                                : true) && (
                                                <Graph
                                                    key={`${chartName}-${index}`}
                                                    graphData={chartsData[chartName]}
                                                    graphName={chartName}
                                                    selectedPeriod={periodSelectorData.selectedPeriod}
                                                    fetchGraph={fetchGraph}
                                                    user={user}
                                                    showAlerts={showAlerts}
                                                    initialiseHeadlinePanelPolling={initialiseHeadlinePanelPolling}
                                                    isPDFReport={isPDFReport}
                                                />
                                            )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )
                ) : (
                    renderTable()
                )}
            </div>
        </>
    );
}

export default GstAnalysisPanel;
